import { useCallback, useEffect, useState } from 'react'
import { api, errorMessage } from '../api/client'

interface Loaded<T> {
  data: T | null
  loading: boolean
  error: string | null
  reload: () => void
  set: (value: T | null) => void
}

interface Remembered {
  value: unknown
  /** When it was fetched, so staleness is a question with an answer. */
  at: number
}

/**
 * Lists already fetched once in this sitting, kept by their address.
 *
 * <p>In memory only, and deliberately so. These rows carry every customer's name
 * and exactly what each of them owes; written to the phone's storage they would
 * outlive the sitting, survive signing out, and sit readable on a device that
 * may be the counter's rather than the owner's. Closing the tab must take them
 * with it, so this is a plain Map and never localStorage.
 */
const sessionCache = new Map<string, Remembered>()

/**
 * How long a remembered list is taken at its word, in milliseconds.
 *
 * <p>Inside this window the screen uses what it has and does not ask at all,
 * which is the whole point: walking through five screens in a minute now costs
 * one request for the customer list instead of five. Outside it the list still
 * appears instantly and is refreshed behind - stale for a moment, never stale
 * for long.
 *
 * <p>A minute, because of the second phone. The counter can add a customer on
 * his own device, and the owner's copy has to pick that up without anybody
 * signing out. Sixty seconds is short enough that nobody notices the gap and
 * long enough that moving between screens is free.
 */
const FRESH_FOR = 60_000

/** Emptied on sign-out, so the next person at this phone starts with nothing. */
export function clearApiCache(): void {
  sessionCache.clear()
}

/**
 * Loads one thing from the API and keeps it fresh.
 *
 * Pass null as the url to hold off entirely - several screens here deliberately
 * show nothing until a name or a date has been chosen, rather than opening with
 * every record ever written and asking the reader to find the one they want.
 *
 * <p><b>cache</b> is opt-in, and only for the long lists that barely change -
 * the names behind the pickers. Those were fetched again on every single screen
 * that carries a picker, which is most of them, so the shop waited for a hundred
 * and forty customers and their balances before it could type a name. With this
 * they appear at once from the last fetch and are quietly refreshed behind.
 *
 * <p>Everything else stays uncached on purpose. A bill, a balance, a day's
 * takings - showing a remembered figure for even a moment while the real one
 * loads is how somebody reads a number that is no longer true, and this is a
 * book of money.
 */
export function useApi<T>(
  url: string | null,
  deps: unknown[] = [],
  options: { cache?: boolean } = {},
): Loaded<T> {
  const cacheable = Boolean(options.cache) && Boolean(url)
  const remembered = cacheable ? sessionCache.get(url as string) : undefined

  const [data, setData] = useState<T | null>((remembered?.value as T | undefined) ?? null)
  // Only "loading" when there is nothing to show. With a remembered list the
  // screen is already usable, and the refresh behind it is nobody's business.
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  /*
   * Asking again has to actually ask.
   *
   * <p>Every write on these screens finishes by calling this, and a customer
   * whose balance just moved is the one case where the remembered figure is
   * certainly wrong. Marking the entry old rather than dropping it means the
   * names stay on screen while the new numbers arrive - dropping it would blink
   * the list empty after every single sale.
   */
  const reload = useCallback(() => {
    const entry = url ? sessionCache.get(url) : undefined
    if (entry) entry.at = 0
    setNonce((n) => n + 1)
  }, [url])

  useEffect(() => {
    if (!url) {
      setData(null)
      setError(null)
      return
    }

    let cancelled = false

    const known = cacheable ? sessionCache.get(url) : undefined
    if (known) {
      // There is something to show, so nothing is waiting - and this has to be
      // said out loud: switching from a url still in flight to one already
      // remembered would otherwise leave "loading" stuck on for good.
      setData(known.value as T)
      setLoading(false)
    } else {
      setLoading(true)
    }
    setError(null)

    // Fetched a moment ago by another screen: nothing to ask.
    if (known && Date.now() - known.at < FRESH_FOR) return

    api
      .get<T>(url)
      .then((response) => {
        if (cancelled) return
        if (cacheable) sessionCache.set(url, { value: response.data, at: Date.now() })
        setData(response.data)
      })
      .catch((err) => {
        // A failed refresh behind a list already on screen is not worth shouting
        // about - the names are still there and still usable.
        if (!cancelled && !known) setError(errorMessage(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, nonce, ...deps])

  return { data, loading, error, reload, set: setData }
}
